// =============================================================================
// utils/aggregate.js
//
// Toda a lógica de derivação e agregação do Dashboard. Deliberadamente espelha
// as regras já existentes na view ActivityTracker (utils/status.js e o
// SIDE_BUCKET do TrackerTable) para que os números batam certo com a tabela.
// =============================================================================

// ── Estado da atividade (igual a ActivityTracker/utils/status.js) ────────────
export const STATUS_VALUES = ['In Progress', 'Planned', 'Overdue', 'Closed']

const OVERDUE_GRACE_MS = 14 * 24 * 60 * 60 * 1000 // 2 semanas de tolerância

const parseMs = (raw) => {
  if (!raw) return null
  const s = String(raw).trim()

  const slash = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/)
  if (slash) return new Date(`${slash[3]}-${slash[1]}-${slash[2]}T00:00:00Z`).getTime()

  const dash = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (dash) {
    const [, y, a, b] = dash
    const iso = Number(a) > 12 ? `${y}-${b}-${a}` : `${y}-${a}-${b}`
    return new Date(`${iso}T00:00:00Z`).getTime()
  }

  const d = new Date(s)
  return isNaN(d.getTime()) ? null : d.getTime()
}

export const getDisplayStatus = (entry) => {
  if ((entry.status ?? 'Open') === 'Closed') return 'Closed'
  const now   = Date.now()
  const start = parseMs(entry.startDate)
  const end   = parseMs(entry.endDate)
  if (end != null && now > end + OVERDUE_GRACE_MS) return 'Overdue'
  if (start != null && start > now) return 'Planned'
  return 'In Progress'
}

// ── Go / No-Go (Item 22) ─────────────────────────────────────────────────────
// A decisão de rollout só existe em atividades fechadas (rolloutDecision:
//   true → Go, false → No-Go, null/undefined → sem decisão, não conta).
export const ROLLOUT_VALUES = ['Go', 'No-Go']

export const getRolloutDecision = (entry) => {
  if (entry.rolloutDecision === true)  return 'Go'
  if (entry.rolloutDecision === false) return 'No-Go'
  return null
}

// ── Side / Cell_Owner ────────────────────────────────────────────────────────
// Valores canónicos do Bulk Import. A atividade pode ter mais que um, derivado
// dos sites gravados (servers[].tacs[].siteSides).
export const SIDE_VALUES = ['TFinTF', 'TFinVF', 'VFinTF']

export const getEntrySides = (entry) => {
  const set = new Set()
  ;(entry.servers ?? []).forEach((s) =>
    (s.tacs ?? []).forEach((t) =>
      Object.values(t.siteSides ?? {}).forEach((side) => {
        if (side) set.add(side)
      })
    )
  )
  return [...set]
}

// ── Nº de sites de uma atividade ─────────────────────────────────────────────
// Conta sites distintos a partir do mapa siteToSide de cada server; se não
// existir, cai para o comprimento de servers.
export const getSiteCount = (entry) => {
  const servers = entry.servers ?? []
  let total = 0
  let hadMap = false
  servers.forEach((s) => {
    const map = s.siteToSide ?? (s.tacs ?? []).reduce((acc, t) => ({ ...acc, ...(t.siteSides ?? {}) }), null)
    if (map) { hadMap = true; total += Object.keys(map).length }
  })
  return hadMap ? total : servers.length
}

// ── Owner(s) de uma atividade ────────────────────────────────────────────────
export const getEntryOwners = (entry) => {
  if (entry.ownerNames?.length) return entry.ownerNames
  if (entry.ownerName) return [entry.ownerName]
  return []
}

// ── Dias de atraso (só faz sentido para Overdue) ─────────────────────────────
export const getOverdueDays = (entry) => {
  const end = parseMs(entry.endDate)
  if (end == null) return 0
  const diff = Date.now() - (end + OVERDUE_GRACE_MS)
  return diff > 0 ? Math.floor(diff / (24 * 60 * 60 * 1000)) : 0
}

// ── Idade em dias desde a StartDate (para o filtro de período) ────────────────
export const getAgeDays = (entry) => {
  const start = parseMs(entry.startDate)
  if (start == null) return Infinity
  return Math.floor((Date.now() - start) / (24 * 60 * 60 * 1000))
}

// ── Contadores genéricos ─────────────────────────────────────────────────────
// countBy: soma por chave derivada (função). Para campos multi-valor (side,
// owner) a função devolve um array e cada valor conta uma vez.
export const countBy = (rows, keyFn, universe) => {
  const acc = Object.fromEntries((universe ?? []).map((k) => [k, 0]))
  rows.forEach((r) => {
    const v = keyFn(r)
    const vals = Array.isArray(v) ? v : [v]
    vals.forEach((x) => {
      if (x == null || x === '') return
      acc[x] = (acc[x] ?? 0) + 1
    })
  })
  return acc
}

// Extrai o universo de valores distintos presentes nos dados (para popular
// dropdowns dinamicamente em vez de hard-coded).
export const distinctValues = (rows, keyFn) => {
  const set = new Set()
  rows.forEach((r) => {
    const v = keyFn(r)
    const vals = Array.isArray(v) ? v : [v]
    vals.forEach((x) => { if (x != null && x !== '') set.add(x) })
  })
  return [...set].sort((a, b) => String(a).localeCompare(String(b)))
}
